import { Button } from '@creditornot/cb-button';
import { DataTable } from '@creditornot/cb-data-table';
import { colors, cbSemanticCssVars, typographyCss } from '@creditornot/cb-ingredients';
import { ModalContent, ModalFooter, ModalHeader } from '@creditornot/cb-modal';
import { useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import React, { useMemo, useRef } from 'react';
import Barcode from 'react-barcode';
import { FormattedMessage } from 'react-intl';
import { useReactToPrint } from 'react-to-print';
import styled from 'styled-components';

import { readCompletedPurchases } from 'apis/restaurant-api/merchant-admin/purchases/api';
import { Purchase } from 'apis/restaurant-api/merchant-admin/purchases/types';
import { WoltModal } from 'common/components';
import MonetaryAmount from 'common/formatting/MonetaryAmount';
import { useLocaleUtils } from 'common/hooks';
import { Typography as T } from 'common/typography';

import { aggregateItemDataFromPurchases } from './utils';

const MAX_PURCHASES_PER_REQUEST = 500;

type Props = {
  range: { from: string; to: string };
  venueId: string;
  venueName: string;
  onClose: () => void;
};

const StyledModal = styled(WoltModal)`
  background-color: ${cbSemanticCssVars.surfaceMain};
  width: 640px;
`;

const ModalRoot = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledModalContent = styled(ModalContent)`
  flex: 1;
  overflow: auto;
  padding: 16px 24px;
`;

const ContentHeader = styled.div`
  display: flex;
`;

const HeaderInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  :not(:first-child) {
    margin-inline-start: 32px;
  }
`;

const InfoTitle = styled(T.Body3)`
  color: ${cbSemanticCssVars.textSecondary};
`;

const Info = styled(T.Body3)`
  color: ${cbSemanticCssVars.textPrimary};
`;

const StyledDataTable: typeof DataTable = styled(DataTable)`
  margin-block-start: 24px;
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
`;

const BarcodeContainer = styled.div`
  position: relative;
  height: 48px;
  margin: -6px 0;
  text-align: center;
`;

const BarcodePlaceholder = styled.div`
  ${typographyCss.Small};
  color: ${cbSemanticCssVars.textSecondary};
  text-align: center;
`;

const TotalRow = styled.div`
  ${typographyCss.Title2};
  padding: 0 16px;
  height: 48px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
`;

const StyledModalFooter = styled(ModalFooter)`
  justify-content: flex-end;
  padding-inline-end: 98px;
`;

const recursiveReadCompletedPurchases = async (
  payload: {
    venueId: string;
    startTime: string;
    endTime: string;
    limit: number;
    cursor?: string;
  },
  previousPurchases: Purchase[] = [],
): Promise<Purchase[]> => {
  const res = await readCompletedPurchases(payload);
  if (!res.metadata?.next) {
    return [...previousPurchases, ...res.data.purchases];
  }
  const nextPurchases = await recursiveReadCompletedPurchases(
    { ...payload, cursor: res.metadata.next },
    res.data.purchases,
  );
  return [...previousPurchases, ...nextPurchases];
};

const BarcodesModal = ({ range, venueId, venueName, onClose }: Props) => {
  const printableContentRef = useRef<HTMLSpanElement>(null);

  const { getLocalizedUnit } = useLocaleUtils();

  const { data: purchases, isLoading } = useQuery(['purchase-barcodes', venueId, range.from, range.to], () =>
    recursiveReadCompletedPurchases({
      venueId,
      startTime: range.from,
      endTime: range.to,
      limit: MAX_PURCHASES_PER_REQUEST,
    }),
  );

  const printBarcodes = useReactToPrint({
    content: () => printableContentRef.current,
    // enforces pepper text color for printing - ignore-color
    pageStyle: `* { color: ${colors.pepper100} } @page { margin: 48px }`,
  });

  const itemAggregatedData = useMemo(() => aggregateItemDataFromPurchases(purchases || []), [purchases]);

  const totalAmount = useMemo(
    () =>
      itemAggregatedData.length > 0
        ? itemAggregatedData.reduce<{ amount: number; currency: Currency }>(
            (acc, curr) => ({ amount: acc.amount + Number(curr.price.amount), currency: curr.price.currency }),
            { amount: 0, currency: 'EUR' },
          )
        : undefined,
    [itemAggregatedData],
  );

  return (
    <StyledModal isOpen onRequestClose={onClose} position="right">
      <ModalRoot>
        <ModalHeader onCloseClick={onClose}>
          <FormattedMessage id="purchases.barcodes.title" />
        </ModalHeader>
        <StyledModalContent>
          <span ref={printableContentRef}>
            <ContentHeader>
              <HeaderInfo>
                <InfoTitle>
                  <FormattedMessage id="purchases.barcodes.date" />
                </InfoTitle>
                <Info>{`${DateTime.fromISO(range.from).toLocaleString(DateTime.DATE_MED)} - ${DateTime.fromISO(
                  range.to,
                ).toLocaleString(DateTime.DATE_MED)}`}</Info>
              </HeaderInfo>
              <HeaderInfo>
                <InfoTitle>
                  <FormattedMessage id="purchases.barcodes.venue" />
                </InfoTitle>
                <Info>{venueName}</Info>
              </HeaderInfo>
            </ContentHeader>
            <StyledDataTable
              getRowKey={record => record.id}
              dataSource={itemAggregatedData}
              loading={isLoading}
              columns={[
                { id: 'item-name', name: 'item name', render: ({ record }) => <T.Small>{record.name}</T.Small> },
                {
                  id: 'quantity',
                  name: 'quantity',
                  render: ({ record }) => {
                    if (record.weightInGrams) {
                      return record.weightInGrams >= 1000 ? (
                        <T.Small>
                          {(record.weightInGrams / 1000).toFixed(2)}
                          {getLocalizedUnit('kilogram')}
                        </T.Small>
                      ) : (
                        <T.Small>
                          {record.weightInGrams}
                          {getLocalizedUnit('gram')}
                        </T.Small>
                      );
                    }
                    return <T.Small>{record.count}</T.Small>;
                  },
                },
                {
                  id: 'price',
                  name: 'price',
                  render: ({ record }) => (
                    <T.Small>
                      <MonetaryAmount amount={Number(record.price.amount)} currency={record.price.currency} />
                    </T.Small>
                  ),
                },
                {
                  id: 'barcode',
                  name: 'barcode',
                  render: ({ record }) =>
                    record.barcode ? (
                      <BarcodeContainer>
                        <Barcode
                          value={record.barcode}
                          width={1}
                          height={38}
                          fontSize={10}
                          textMargin={0}
                          marginTop={2}
                          marginRight={4}
                          marginBottom={1}
                          marginLeft={4}
                        />
                      </BarcodeContainer>
                    ) : (
                      <BarcodePlaceholder>-</BarcodePlaceholder>
                    ),
                },
              ]}
            />
            {totalAmount && (
              <TotalRow>
                <div>Total</div>
                <MonetaryAmount {...totalAmount} />
              </TotalRow>
            )}
          </span>
        </StyledModalContent>
        <StyledModalFooter>
          <Button variant="blue" size="small" onClick={printBarcodes} disabled={!itemAggregatedData.length}>
            <FormattedMessage id="purchases.barcodes.print" />
          </Button>
        </StyledModalFooter>
      </ModalRoot>
    </StyledModal>
  );
};

export default BarcodesModal;
